import React, { useEffect, useState, memo } from 'react';
import { graphql } from 'gatsby';
import { OmTwoColumnImageRight } from '@om/component-library-react';
import { useLocation } from '@reach/router';

const PersonalisedTwoColumnImageRight = memo(({ twoColumnImageRightContent, slot }) => {
    const [params, setParams] = useState({});
    const routerLocation = useLocation();

    // Update URL parameters only if they change
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const paramSearch = new URLSearchParams(routerLocation.search);
            const param1 = paramSearch.get('omcustomername');
            const param2 = paramSearch.get('omproduct');

            setParams((prevParams) => {
                if (prevParams.param1 !== param1 || prevParams.param2 !== param2) {
                    return { param1, param2 };
                }
                return prevParams;
            });
        }
    }, [routerLocation.search]);

    if (!twoColumnImageRightContent) return null;

    const {
        uid,
        has_boundary,
        image_url,
        image_title,
        image_alt,
        video,
        video_url,
        button_one_action_type,
        call_me_back_primary,
        call_me_back_secondary,
        button_one_text,
        button_one_url,
        button_one_open_tab,
        button_two_action_type,
        button_two_text,
        button_two_url,
        button_two_open_tab,
        button_three_url,
        button_three_image_url,
        button_three_open_tab,
        button_four_url,
        button_four_image_url,
        button_four_open_tab,
        use_heading_as_h1,
        text_heading,
        text_content,
        text_sub_title,
        podcast_episode_id,
        podcast_series_id,
        roa_quote_tool_primary,
        roa_quote_tool_secondary,
    } = twoColumnImageRightContent;

    return (
        <OmTwoColumnImageRight
            slot={slot}
            id={uid}
            hasBoundary={has_boundary}
            buttonOneActionType={button_one_action_type}
            buttonOneText={button_one_text}
            buttonOneUrl={button_one_url ? button_one_url.href : ''}
            callMeBackPrimary={call_me_back_primary ? JSON.stringify(call_me_back_primary[0]) : ''}
            callMeBackSecondary={call_me_back_secondary ? JSON.stringify(call_me_back_secondary[0]) : ''}
            roaQuoteToolPrimary={roa_quote_tool_primary ? JSON.stringify(roa_quote_tool_primary[0]) : ''}
            roaQuoteToolSecondary={roa_quote_tool_secondary ? JSON.stringify(roa_quote_tool_secondary[0]) : ''}
            buttonTwoActionType={button_two_action_type}
            buttonTwoText={button_two_text}
            buttonTwoUrl={button_two_url ? button_two_url.href : ''}
            buttonThreeUrl={button_three_url ? button_three_url.href : ''}
            buttonThreeImageUrl={button_three_image_url ? button_three_image_url.url : ''}
            buttonFourUrl={button_four_url ? button_four_url.href : ''}
            buttonFourImageUrl={button_four_image_url ? button_four_image_url.url : ''}
            podcastSeriesId={podcast_series_id}
            podcastEpisodeId={podcast_episode_id}
            buttonOneOpenTab={button_one_open_tab}
            buttonTwoOpenTab={button_two_open_tab}
            buttonThreeOpenTab={button_three_open_tab}
            buttonFourOpenTab={button_four_open_tab}
        >
            {text_heading && params.param1 ? (
                use_heading_as_h1 ? (
                    <h1
                        style={{ fontWeight: 'normal', color: 'green' }}
                        slot="text-heading"
                        dangerouslySetInnerHTML={{
                            __html: params.param1 + " " +  text_heading.substr(3, text_heading.length -7 ),
                        }}
                    />
                ) : (
                    <span
                        style={{ fontWeight: 'normal', color: 'green' }}
                        slot="text-heading"
                        dangerouslySetInnerHTML={{
                            __html: params.param1 + " " +   text_heading.substr(3, text_heading.length -7 ),
                        }}
                    />
                )
            ) : text_heading ? (
                use_heading_as_h1 ? (
                    <h1
                        style={{ fontWeight: 'normal', color: 'green' }}
                        slot="text-heading"
                        dangerouslySetInnerHTML={{
                            __html: 'We’re delighted to have you with us. Your trust in us is truly appreciated.',
                        }}
                    />
                ) : (
                    <span
                        style={{ fontWeight: 'normal', color: 'green' }}
                        slot="text-heading"
                        dangerouslySetInnerHTML={{
                            __html: 'We’re delighted to have you with us. Your trust in us is truly appreciated.',
                        }}
                    />
                )
            ) : null}
            {text_content && params.param2 ? (
                <span
                    slot="text-content"
                    dangerouslySetInnerHTML={{
                        __html: `<p><strong>${params.param2}</strong> ${text_content.substr(3)}`,
                    }}
                />
            ) : text_content ? (
                <span
                    slot="text-content"
                    dangerouslySetInnerHTML={{
                        __html: `<p><strong>Your financial wellbeing is in safe hands</strong> ${text_content.substr(
                            3
                        )}`,
                    }}
                />
            ) : null}
            {text_sub_title && (
                <span slot="text-sub-title" dangerouslySetInnerHTML={{ __html: text_sub_title }} />
            )}
            {image_url && (
                <img
                    loading="lazy"
                    slot="image"
                    title={image_title}
                    alt={image_alt}
                    src={image_url.url}
                />
            )}
            {video && !video_url && !image_url && (
                <iframe slot="image" src={video.url} frameBorder="0" allow="fullscreen" />
            )}
            {video_url && !video && !image_url && (
                <iframe
                    slot="image"
                    title={video_url.title}
                    src={video_url.href}
                    frameBorder="0"
                    allow="fullscreen"
                />
            )}
        </OmTwoColumnImageRight>
    );
});

export const pageQuery = graphql`
    fragment PersonalisedTwoColumnImageRight on Contentstack_personalised_2_col_image_right {
        id
        uid
        has_boundary
        use_heading_as_h1
        podcast_episode_id
        podcast_series_id
        text_heading
        text_sub_title
        text_content
        video_url {
            ...Link
        }
        video {
            ...File
        }
        image_url {
            ...File
        }
        image_title
        image_alt
        button_one_text
        button_one_url {
            ...Link
        }
        button_one_action_type
        button_one_open_tab
        call_me_back_primary {
            ...CallMeBack
        }
        call_me_back_secondary {
            ...CallMeBack
        }
        button_two_text
        button_two_url {
            ...Link
        }
        button_two_action_type
        button_two_open_tab
        button_three_url {
            ...Link
        }
        button_three_image_url {
            ...File
        }
        button_three_open_tab
        button_four_url {
            ...Link
        }
        button_four_image_url {
            ...File
        }
        button_four_open_tab
        roa_quote_tool_primary {
            ...RoaQuoteTool
        }
        roa_quote_tool_secondary {
            ...RoaQuoteTool
        }
    }
`;

export default PersonalisedTwoColumnImageRight;
